import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from '@com/core/common/translations/i18n';
import { MARKETPLACE_CONFIG } from '@com/core/common/utils/config';
import { AppContext } from '@com/core/common/contexts/appContext';
import OutsideClickHandler from 'react-outside-click-handler';
import Link from '@com/core/common/components/Link';
import { getShippingEstimatorType } from '@com/core/common/utils/product';
import { TFbtOffer, THit, TOffer } from '@com/core/common/types/catalog';
import { IReturnableItem, IClaimItem } from '@com/core/common/types/customer';
import { IOrderItem } from '@com/core/common/types/orders';
import { TProductCart } from '@com/core/common/types/cart';

type TProps = {
    tooltip?: boolean;
    type?: string;
    product?: THit | TOffer | IOrderItem | IReturnableItem | IClaimItem | TProductCart | TFbtOffer;
};

const FulfilmentBadge = ({ product, tooltip = false, type = null }: TProps): JSX.Element | null => {
    const { t } = useTranslation('common');
    const [showTooltip, setShowTooltip] = useState(false);
    const { locale } = useContext(AppContext);
    const offer = product as TOffer;
    let badgeImgName = null;
    let badgeUrl: string;
    let tooltipTextKey = '';
    let src = null;

    let fulfilmentType = '';
    if (type) {
        fulfilmentType = type;
    } else if (product?.flags.length > 0) {
        fulfilmentType = getShippingEstimatorType(product.flags);
    } else if (offer?.isFbn) {
        fulfilmentType = 'fbn';
    }

    switch (fulfilmentType) {
        case 'preorder':
            badgeImgName = 'pre-ordering';
            badgeUrl = '/preorder';
            tooltipTextKey = '_product_pre-ordering-desc';
            src =
                locale.lang === 'ar'
                    ? MARKETPLACE_CONFIG.assets.images.fulfilmentPreOrderAr
                    : MARKETPLACE_CONFIG.assets.images.fulfilmentPreOrderEn;
            break;
        case 'fbn':
            badgeImgName = 'noon-express';
            badgeUrl = '/noon-express';
            tooltipTextKey = '_product_noon-now-desc';
            src =
                locale.lang === 'ar'
                    ? MARKETPLACE_CONFIG.assets.images.fulfilmentExpressAr
                    : MARKETPLACE_CONFIG.assets.images.fulfilmentExpressEn;
            break;
        case 'marketplace':
            badgeImgName = 'noon-marketplace';
            badgeUrl = '/noon-market';
            tooltipTextKey = '_product_noon-marketplace-desc';
            src =
                locale.lang === 'ar'
                    ? MARKETPLACE_CONFIG.assets.images.fulfilmentMarketplaceAr
                    : MARKETPLACE_CONFIG.assets.images.fulfilmentMarketplaceEn;
            break;
        default:
            return null;
    }

    const toggleToolTip = (): void => {
        setShowTooltip(openState => !openState);
    };

    return (
        <Container>
            <img src={src} alt={badgeImgName} className={type} height="20px" width="80px" />
            {tooltip && (
                <TooltipWrapper>
                    <button
                        type="button"
                        className="tooltipTrigger"
                        onClick={toggleToolTip}
                        aria-label={t('help-center')}>
                        <img src={MARKETPLACE_CONFIG.assets.icons.help} alt="help" className={type} />
                    </button>
                    {showTooltip && (
                        <OutsideClickHandler onOutsideClick={toggleToolTip}>
                            <ToolTip>
                                <div className="tooltip_text">
                                    <Trans i18nKey={tooltipTextKey}>
                                        <span>Fast delivery and 100% Authentic.</span>
                                        <Link href={badgeUrl} linkClass="tooltipLink" external>
                                            Learn more
                                        </Link>
                                    </Trans>
                                </div>
                                <div className="tooltip_tip" />
                            </ToolTip>
                        </OutsideClickHandler>
                    )}
                </TooltipWrapper>
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    align-items: center;

    img {
        display: block;
        image-rendering: -webkit-optimize-contrast;
        z-index: 1;
    }
`;

const TooltipWrapper = styled.div`
    position: relative;
    ${({ theme: { left } }): string => `margin-${left}: 4px`};
    display: flex;
    justify-content: center;
    align-items: center;
`;
const ToolTip = styled.div`
    position: absolute;
    ${({ theme: { right } }): string => `${right}: 50%;`}
    transform: ${({ theme: { isRTL } }): string => (isRTL ? 'translateX(-50%)' : 'translateX(50%)')};
    bottom: 35px;
    width: 200px;
    border: 1px solid ${({ theme: { colors } }) => colors.border};
    background-color: ${({ theme: { colors } }) => colors.bgTertiary};
    font-size: 0.825rem;
    z-index: 1;

    &:before,
    &:after {
        display: block;
        content: '';
        position: absolute;
        width: 50%;
        top: 0;
        bottom: 0;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
        transition: transform 200ms ease-out;
    }

    &:before {
        left: 0;
        border-bottom-right-radius: 1rem;
    }

    &:after {
        right: 0;
        border-bottom-left-radius: 1rem;
    }

    .tooltip_text{
        position: relative;
        padding: 6px 10px;
        width: 100%;
        height: 100%;
        z-index: 3;
        background-color: ${({ theme: { colors } }) => colors.bgTertiary};
    }

    .tooltip_tip {
        position: absolute;
        display: block;
        width: 1rem;
        height: 1rem;
        background: green;
        z-index: 2;
        background-color: ${({ theme: { colors } }) => colors.bgTertiary};
        left: 50%;
        bottom: -0.5rem;
        transform: translateX(-50%) rotate(45deg);
        box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.1);
    }
    a {
        color: ${({ theme: { colors } }) => colors.generalLink};
        padding-top: 7px;
        display: block;
        z-index: 2;
    }
`;

export default FulfilmentBadge;
